import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef, useState } from "react";
import { Button as AriaButton, Menu, MenuItem, Popover } from "react-aria-components";

import actionsSeparator from "@/assets/figma/chat-context-bar/actions-separator.svg";
import closeIcon from "@/assets/figma/chat-context-bar/close.svg";
import externalLinkIcon from "@/assets/figma/chat-context-bar/external-link.svg";
import pointsSeparator from "@/assets/figma/chat-context-bar/points-separator.svg";
import quoteIcon from "@/assets/figma/chat-context-bar/quote.svg";
import resourceProgress from "@/assets/figma/chat-context-bar/resource-progress.svg";
import { cn } from "@/shared/lib/cn";
import {
  getMerryChatContextBarVariant,
  MERRY_CHAT_CONTEXT_BAR_COMPONENT_ID,
} from "@/shared/ui/merryChatContextBarVariants";

gsap.registerPlugin(useGSAP);

export type MerryChatContextBarResourceState = "exhausted" | "low";
export type MerryChatContextBarBillingTarget = "credits" | "plans" | "storage";

export interface MerryChatContextBarResourceMetric {
  current: string;
  label: string;
  percent: string;
  state: MerryChatContextBarResourceState;
  total: string;
  unit: string;
}

export interface MerryChatContextBarResourceContent {
  action:
    | { mode: "direct"; target: Exclude<MerryChatContextBarBillingTarget, "plans"> }
    | { mode: "menu" };
  metrics:
    | readonly [MerryChatContextBarResourceMetric]
    | readonly [MerryChatContextBarResourceMetric, MerryChatContextBarResourceMetric];
  type: "resource";
}

export interface MerryChatContextBarQuoteContent {
  text: string;
  type: "quote";
}

export type MerryChatContextBarContent =
  | MerryChatContextBarResourceContent
  | MerryChatContextBarQuoteContent;

interface MerryChatContextBarCommonProps {
  className?: string;
  dismissAccessibleLabel?: string;
  isVisible?: boolean;
  onDismiss: () => void;
}

export type MerryChatContextBarProps = MerryChatContextBarCommonProps &
  (
    | {
        content: MerryChatContextBarResourceContent;
        onRecharge: (target: MerryChatContextBarBillingTarget) => void;
      }
    | {
        content: MerryChatContextBarQuoteContent;
        onRecharge?: never;
      }
  );

const presenceMotion = {
  enterDuration: 0.22,
  enterEase: "power2.out",
  exitDuration: 0.18,
  exitEase: "power2.in",
} as const;

const rechargeItems: ReadonlyArray<{
  id: MerryChatContextBarBillingTarget;
  label: string;
}> = [
  { id: "plans", label: "套餐升级" },
  { id: "credits", label: "额度充值" },
  { id: "storage", label: "云存储扩容" },
];

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function MerryChatContextBar(props: MerryChatContextBarProps) {
  const {
    className,
    content,
    dismissAccessibleLabel = "关闭提示",
    isVisible = true,
    onDismiss,
  } = props;
  const rootRef = useRef<HTMLDivElement>(null);
  const rechargeTriggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const didInitializeRef = useRef(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const variant = getMerryChatContextBarVariant(content.type);

  useGSAP(
    () => {
      const root = rootRef.current;
      if (!root) return;
      const isFirstRun = !didInitializeRef.current;
      didInitializeRef.current = true;
      gsap.killTweensOf(root);

      if (isFirstRun && !isVisible) {
        root.dataset.chatContextBarAnimating = "false";
        gsap.set(root, { autoAlpha: 0, height: 0 });
        root.hidden = true;
        return;
      }
      if (isVisible) root.hidden = false;
      else if (root.hidden) return;

      if (prefersReducedMotion()) {
        if (isVisible) {
          root.hidden = false;
          gsap.set(root, { clearProps: "height,opacity,visibility,willChange" });
        } else {
          gsap.set(root, { autoAlpha: 0, height: 0 });
          root.hidden = true;
        }
        root.dataset.chatContextBarAnimating = "false";
        return;
      }

      root.dataset.chatContextBarAnimating = "true";
      gsap.set(root, { willChange: "height, opacity" });
      const completeEntrance = () => {
        root.dataset.chatContextBarAnimating = "false";
        gsap.set(root, { clearProps: "height,opacity,visibility,willChange" });
      };

      if (isFirstRun && isVisible) {
        gsap.fromTo(
          root,
          { autoAlpha: 0, height: 0 },
          {
            autoAlpha: 1,
            duration: presenceMotion.enterDuration,
            ease: presenceMotion.enterEase,
            height: variant.height,
            onComplete: completeEntrance,
          },
        );
        return;
      }

      gsap.to(root, {
        autoAlpha: isVisible ? 1 : 0,
        duration: isVisible ? presenceMotion.enterDuration : presenceMotion.exitDuration,
        ease: isVisible ? presenceMotion.enterEase : presenceMotion.exitEase,
        height: isVisible ? variant.height : 0,
        onComplete: () => {
          if (isVisible) completeEntrance();
          else {
            root.dataset.chatContextBarAnimating = "false";
            root.hidden = true;
          }
        },
      });
    },
    { dependencies: [isVisible, variant.height], scope: rootRef },
  );

  useGSAP(
    () => {
      const menu = menuRef.current;
      if (!menu || !isMenuOpen || prefersReducedMotion()) return;
      gsap.fromTo(
        menu,
        { autoAlpha: 0, scale: 0.96 },
        {
          autoAlpha: 1,
          clearProps: "opacity,transform,visibility,willChange",
          duration: 0.18,
          ease: "power2.out",
          scale: 1,
        },
      );
    },
    { dependencies: [isMenuOpen], scope: menuRef },
  );

  const handleRecharge = () => {
    if (content.type !== "resource") return;
    if (content.action.mode === "direct") {
      props.onRecharge?.(content.action.target);
    } else {
      setIsMenuOpen(true);
    }
  };

  const updateMenuOpen = (nextIsOpen: boolean) => {
    if (nextIsOpen) {
      setIsMenuOpen(true);
      return;
    }
    const menu = menuRef.current;
    if (!menu || prefersReducedMotion()) {
      setIsMenuOpen(false);
      return;
    }
    gsap.killTweensOf(menu);
    gsap.to(menu, {
      autoAlpha: 0,
      duration: 0.14,
      ease: "power2.in",
      onComplete: () => setIsMenuOpen(false),
      scale: 0.96,
    });
  };

  return (
    <>
      <div
        aria-hidden={!isVisible || undefined}
        className={cn("merry-chat-context-bar", className)}
        data-chat-context-bar-animating="false"
        data-chat-context-bar-type={content.type}
        data-chat-context-bar-visible={isVisible}
        data-figma-component-id={MERRY_CHAT_CONTEXT_BAR_COMPONENT_ID}
        data-figma-node-id={variant.nodeId}
        inert={!isVisible || undefined}
        ref={rootRef}
      >
        <img alt="" aria-hidden="true" className="merry-chat-context-bar__background" draggable="false" src={variant.backgroundSource} />
        <div className="merry-chat-context-bar__layout">
          {content.type === "resource" ? (
            <div className="merry-chat-context-bar__resources">
              {content.metrics.map((metric, index) => (
                <ResourceMetric isLast={index === content.metrics.length - 1} key={metric.label} metric={metric} />
              ))}
            </div>
          ) : (
            <div className="merry-chat-context-bar__quote">
              <span aria-hidden="true" className="merry-chat-context-bar__quote-icon">
                <img alt="" draggable="false" src={quoteIcon} />
              </span>
              <span className="merry-chat-context-bar__quote-text">{content.text}</span>
            </div>
          )}

          <div className="merry-chat-context-bar__actions">
            {content.type === "resource" ? (
              <>
                <AriaButton
                  aria-expanded={content.action.mode === "menu" ? isMenuOpen : undefined}
                  aria-haspopup={content.action.mode === "menu" ? "menu" : undefined}
                  aria-label="去充值"
                  className="merry-chat-context-bar__recharge"
                  isDisabled={!isVisible}
                  onPress={handleRecharge}
                  ref={rechargeTriggerRef}
                >
                  <span className="merry-chat-context-bar__recharge-label">去充值</span>
                  <span aria-hidden="true" className="merry-chat-context-bar__action-icon">
                    <img alt="" draggable="false" src={externalLinkIcon} />
                  </span>
                </AriaButton>
                <img alt="" aria-hidden="true" className="merry-chat-context-bar__actions-divider" draggable="false" src={actionsSeparator} />
              </>
            ) : null}
            <AriaButton aria-label={dismissAccessibleLabel} className="merry-chat-context-bar__dismiss" isDisabled={!isVisible} onPress={onDismiss}>
              <img alt="" draggable="false" src={closeIcon} />
            </AriaButton>
          </div>
        </div>
        <span aria-hidden="true" className="merry-chat-context-bar__stroke" />
      </div>

      {content.type === "resource" && content.action.mode === "menu" ? (
        <Popover
          className="merry-chat-context-bar-menu"
          isOpen={isMenuOpen}
          offset={8}
          onOpenChange={updateMenuOpen}
          placement="top end"
          shouldFlip
          triggerRef={rechargeTriggerRef}
        >
          <div ref={menuRef}>
            <p className="merry-chat-context-bar-menu__title">去充值</p>
            <Menu
              aria-label="充值选项"
              className="merry-chat-context-bar-menu__items"
              items={rechargeItems}
              onAction={(key) => {
                props.onRecharge?.(key as MerryChatContextBarBillingTarget);
                updateMenuOpen(false);
              }}
            >
              {(item) => (
                <MenuItem className="merry-chat-context-bar-menu__item" id={item.id}>
                  <span>{item.label}</span>
                  <img alt="" aria-hidden="true" src={externalLinkIcon} />
                </MenuItem>
              )}
            </Menu>
          </div>
        </Popover>
      ) : null}
    </>
  );
}

function ResourceMetric({ isLast, metric }: { isLast: boolean; metric: MerryChatContextBarResourceMetric }) {
  return (
    <>
      <div className="merry-chat-context-bar__resource" data-resource-state={metric.state}>
        <span className="merry-chat-context-bar__remaining">
          <span>{metric.label}</span>
          <span>{metric.percent}</span>
        </span>
        <img alt="" aria-hidden="true" className="merry-chat-context-bar__progress" draggable="false" src={resourceProgress} />
        <img alt="" aria-hidden="true" className="merry-chat-context-bar__points-divider" draggable="false" src={pointsSeparator} />
        <span className="merry-chat-context-bar__current-points">{metric.current}</span>
        <span className="merry-chat-context-bar__points-total">
          <span>/</span>
          <span>{metric.total} {metric.unit}</span>
        </span>
      </div>
      {!isLast ? <span aria-hidden="true" className="merry-chat-context-bar__resource-divider" /> : null}
    </>
  );
}
